/**
 * ShellLayout — the persistent operator chrome, mounted as a React Router
 * layout route (renders `<Outlet />`). Because it stays mounted across every
 * navigation between the pages it wraps, the `Sidenav` inside it never
 * unmounts/remounts — so its level transitions animate smoothly instead of
 * flashing on each route change (the reason this exists; cf. deepdubapp
 * `components/Layout/Layout.tsx`).
 *
 * Structure when a route wants the shell:
 *   <NavProvider>            ← the single, URL-derived nav context
 *     <MobileNavProvider>    ← shares drawer open-state with page headers
 *       <div.shell>          ← the grid: sidenav | content
 *         <CommandPalette/>  ← portal, out of grid flow
 *         <Sidenav/>         ← column 1, persistent
 *         <scrim/>           ← mobile only, out of grid flow
 *         <Outlet/>          ← column 2: the page renders <AppShell> (.shell-content)
 *
 * Some routes are "bare" (no shell): the public dial, the full-page workflow
 * editor, the admin console, and the legal pages. For those — and for the bot
 * dial when the visitor is not an authenticated operator — the layout renders
 * a plain `<Outlet />` so the page owns the full viewport.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuthnMe, isAuthnMeConfigured } from '../../AuthnMeProvider';
import { getCustomerIdFromPath } from '../../utils/urlUtils';
import { NavProvider } from '../nav/useNavTree';
import { pageIdForLocation } from '../nav/sidebarLevel';
import { MobileNavProvider } from './MobileNav';
import Sidenav from './Sidenav';
import CommandPalette from './CommandPalette';
import { BotConfigRegistryProvider } from '../data/BotConfigContext';
import { GlobalSidekickProvider } from './GlobalSidekick';

/** Paths that never show the operator shell (the page owns the viewport). */
const BARE_EXACT = new Set(['/privacy', '/terms']);

function botIdFromPath(pathname: string): string {
  const m = pathname.match(/^\/b\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

/**
 * Whether the current route should render the persistent shell. The bot dial
 * (`/b/{id}` exactly) only gets the shell for an authenticated operator; for a
 * public visitor it is the full-screen dial. The workflow editor
 * (`/b/{id}/conversation`) is always bare.
 */
function useShellMode(): 'shell' | 'bare' {
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuthnMe();
  const authed = !isAuthnMeConfigured() || isAuthenticated;
  return useMemo<'shell' | 'bare'>(() => {
    if (BARE_EXACT.has(pathname)) return 'bare';
    if (/^\/b\/[^/]+\/conversation\/?$/.test(pathname)) return 'bare';
    if (/^\/b\/[^/]+\/?$/.test(pathname)) return authed ? 'shell' : 'bare';
    return 'shell';
  }, [pathname, authed]);
}

export default function ShellLayout() {
  const mode = useShellMode();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer on any navigation so a tapped link doesn't leave
  // the drawer covering the page.
  useEffect(() => { setNavOpen(false); }, [location.pathname, location.search]);

  const botId = botIdFromPath(location.pathname);
  const customerId = getCustomerIdFromPath() || undefined;
  const initialPageId = pageIdForLocation(location.pathname, location.search, location.hash);

  if (mode === 'bare') return <Outlet />;

  return (
    <BotConfigRegistryProvider>
      <NavProvider botId={botId || undefined} customerId={customerId} initialPageId={initialPageId}>
        <GlobalSidekickProvider>
          <MobileNavProvider value={{ open: navOpen, setOpen: setNavOpen }}>
            <div className="shell">
              <CommandPalette />
              <Sidenav open={navOpen} />
              {navOpen && <div className="sidenav-scrim" onClick={() => setNavOpen(false)} />}
              <Outlet />
            </div>
          </MobileNavProvider>
        </GlobalSidekickProvider>
      </NavProvider>
    </BotConfigRegistryProvider>
  );
}
